import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/stores';
import { usePermission } from '@/hooks/usePermission';
import {
  Bell, Save, AlertTriangle, Mail, Send, Wifi, WifiOff, Info, X,
  CheckCircle2, XCircle, RefreshCw, Eye, EyeOff, Bot, MessageSquare,
  ShieldCheck, Clock, Users, ArrowRightLeft, ExternalLink, Copy, Check
} from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { isPushSupported, subscribeToPushNotifications, unsubscribeFromPushNotifications } from '@/utils/push-notifications';

const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  if (typeof Notification.requestPermission !== 'function') {
    return Notification.permission;
  }
  
  try {
    const permission = Notification.requestPermission();
    if (permission !== undefined && typeof permission.then === 'function') {
      return await permission;
    }
  } catch (err) {
    console.warn('Promise-based requestPermission failed, trying callback:', err);
  }

  return new Promise<NotificationPermission>((resolve) => {
    try {
      Notification.requestPermission((perm) => {
        resolve(perm);
      });
    } catch (err) {
      console.error('Callback-based requestPermission failed:', err);
      resolve(Notification.permission);
    }
  });
};

interface DeliveryLogItem {
  id?: string;
  subscriberId: string;
  customerName?: string;
  channel: 'WHATSAPP' | 'TELEGRAM' | 'EMAIL' | 'PUSH';
  notificationType: string;
  expiryDate?: string;
  scheduledDaysBefore?: number;
  recipient: string;
  messageText: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED';
  sentAt?: number;
  createdAt?: number;
  externalMessageId?: string;
  errorCode?: string;
  errorMessage?: string;
}

const AlertsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme, language } = useThemeStore();
  const isLight = theme === 'light';
  const isRTL = language === 'ar';
  const { hasPermission } = usePermission();
  const canManageSettings = hasPermission('settings.manage');

  const [activeTab, setActiveTab] = useState<'telegram' | 'whatsapp' | 'system' | 'logs'>('telegram');

  const [activeChannel, setActiveChannel] = useState<'WHATSAPP' | 'TELEGRAM' | 'DISABLED'>('TELEGRAM');
  const [pendingChannelSwitch, setPendingChannelSwitch] = useState<'WHATSAPP' | 'TELEGRAM' | 'DISABLED' | null>(null);
  const [showChannelModal, setShowChannelModal] = useState(false);

  const [email, setEmail] = useState(localStorage.getItem('notification_email') || '');
  const [enabled, setEnabled] = useState(localStorage.getItem('notification_email_enabled') !== 'false');
  const [desktopNotifications, setDesktopNotifications] = useState(localStorage.getItem('notification_desktop_enabled') === 'true');
  const [soundAlerts, setSoundAlerts] = useState(localStorage.getItem('notification_sound_enabled') !== 'false');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendLogs, setSendLogs] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('notification_logs');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [whatsappStatus, setWhatsappStatus] = useState<'disconnected' | 'connecting' | 'connected'>(
    (localStorage.getItem('whatsapp_status') as any) || 'disconnected'
  );
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState(localStorage.getItem('whatsapp_phone') || '');
  const [isGeneratingWhatsapp, setIsGeneratingWhatsapp] = useState(false);
  const [whatsappSessionId, setWhatsappSessionId] = useState('');
  const [whatsappQrCode, setWhatsappQrCode] = useState('');
  const [whatsappLogs, setWhatsappLogs] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('whatsapp_logs');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [whatsappTemplates, setWhatsappTemplates] = useState({
    renewal: localStorage.getItem('whatsapp_tpl_renewal') || 'عزيزنا المشترك {اسم_المشترك}، تم تجديد اشتراكك بنجاح في باقة {اسم_الباقة}. شكراً لاختيارك سيتي تيليكوم.',
    looming: localStorage.getItem('whatsapp_tpl_looming') || 'تنبيه: عزيزنا المشترك {اسم_المشترك}، نود إعلامك بأن اشتراكك في باقة {اسم_الباقة} سينتهي بتاريخ {تاريخ_الانتهاء}. يرجى التجديد لتفادي انقطاع الخدمة.',
    expired: localStorage.getItem('whatsapp_tpl_expired') || 'تنبيه: عزيزنا المشترك {اسم_المشترك}، لقد انتهى اشتراكك في باقة {اسم_الباقة} بتاريخ {تاريخ_الانتهاء}. يرجى تعبئة الرصيد لتفعيل الخدمة مرة أخرى.',
    days3: localStorage.getItem('whatsapp_tpl_days3') || 'تنبيه: عزيزنا المشترك {اسم_المشترك}، نود إعلامك بأن اشتراكك في باقة {اسم_الباقة} سينتهي بعد 3 أيام بتاريخ {تاريخ_الانتهاء}. يرجى التجديد لتفادي انقطاع الخدمة.',
  });

  const [whatsappToggles, setWhatsappToggles] = useState({
    start: localStorage.getItem('whatsapp_toggle_start') !== 'false',
    hours24: localStorage.getItem('whatsapp_toggle_hours24') !== 'false',
    expiredAuto: localStorage.getItem('whatsapp_toggle_expiredAuto') !== 'false',
    days3: localStorage.getItem('whatsapp_toggle_days3') !== 'false',
  });

  const [telegramToken, setTelegramToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const [telegramBotName, setTelegramBotName] = useState('');
  const [telegramBotStatus, setTelegramBotStatus] = useState<'disconnected' | 'checking' | 'connected'>('disconnected');
  const [telegramStatusMessage, setTelegramStatusMessage] = useState('');
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);

  const [telegramTemplates, setTelegramTemplates] = useState({
    days3: localStorage.getItem('tg_tpl_days3') || 'مرحباً {{subscriber_name}} 👋\n\nنود تذكيرك بأن اشتراك الإنترنت الخاص بك في باقة {{plan_name}} سينتهي بعد 3 أيام:\n\n📅 تاريخ الانتهاء: {{expiry_date}}\n⏳ المتبقي: {{days_remaining}} أيام\n\nيرجى التجديد لتفادي انقطاع الخدمة.',
    looming: localStorage.getItem('tg_tpl_looming') || 'مرحباً {{subscriber_name}} 👋\n\nنود تذكيرك بأن اشتراك الإنترنت الخاص بك في باقة {{plan_name}} سينتهي خلال 24 ساعة:\n\n📅 تاريخ الانتهاء: {{expiry_date}}\n⏳ المتبقي: {{days_remaining}} يوم\n\nيرجى التجديد لتجنب انقطاع الخدمة.',
    expired: localStorage.getItem('tg_tpl_expired') || 'تنبيه هام للمشترك {{subscriber_name}} ⚠️\n\nلقد انتهى اشتراك الإنترنت الخاص بك في باقة {{plan_name}} بتاريخ {{expiry_date}}.\n\nيرجى تسديد الاشتراك لإعادة تفعيل الخدمة فوراً.',
    renewal: localStorage.getItem('tg_tpl_renewal') || 'مرحباً {{subscriber_name}} 👋\n\nتم تجديد اشتراك الإنترنت الخاص بك بنجاح في باقة {{plan_name}}.\n\nشكراً لاختيارك خدماتنا 🌐',
  });

  const [activeTgTemplateKey, setActiveTgTemplateKey] = useState<'days3' | 'looming' | 'expired' | 'renewal'>('days3');

  const [telegramToggles, setTelegramToggles] = useState({
    start: localStorage.getItem('tg_toggle_start') !== 'false',
    days3: localStorage.getItem('tg_toggle_days3') !== 'false',
    hours24: localStorage.getItem('tg_toggle_hours24') !== 'false',
    expiredAuto: localStorage.getItem('tg_toggle_expiredAuto') !== 'false',
  });

  const [testChatId, setTestChatId] = useState('');
  const [testMessageText, setTestMessageText] = useState('هذه رسالة اختبارية من بوابة مقاولي FTTH Nexus للتحقق من وصول تنبيهات Telegram بنجاح! 🚀');
  const [isSendingTgTest, setIsSendingTgTest] = useState(false);
  const [tgTestResult, setTgTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const [telegramStats, setTelegramStats] = useState({
    linkedCount: 0,
    sentToday: 0,
    failedToday: 0,
    skippedCount: 0,
    totalDispatched: 0
  });

  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLogItem[]>([]);
  const [logsFilterChannel, setLogsFilterChannel] = useState<'ALL' | 'TELEGRAM' | 'WHATSAPP'>('ALL');
  const [logsFilterStatus, setLogsFilterStatus] = useState<'ALL' | 'SENT' | 'FAILED' | 'SKIPPED'>('ALL');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  useEffect(() => {
    loadServerSettings();
    loadTelegramStats();
    loadDeliveryLogs();
  }, []);

  const loadServerSettings = async () => {
    try {
      const res = await apiClient.get<any>('/api/notifications/settings');
      if (res.data) {
        const s = res.data;
        if (s.notification_channel) {
          setActiveChannel(s.notification_channel);
        } else if (s.telegram_enabled) {
          setActiveChannel('TELEGRAM');
        } else if (s.whatsapp_enabled) {
          setActiveChannel('WHATSAPP');
        } else {
          setActiveChannel('DISABLED');
        }

        if (s.telegram_bot_token) {
          setTelegramToken(s.telegram_bot_token);
        }
        if (s.telegram_bot_username) {
          setTelegramBotUsername(s.telegram_bot_username);
          setTelegramBotStatus('connected');
        }
        if (s.telegram_bot_name) {
          setTelegramBotName(s.telegram_bot_name);
        }
        if (s.telegram_templates) {
          setTelegramTemplates(s.telegram_templates);
        }
        if (s.telegram_toggles) {
          setTelegramToggles(s.telegram_toggles);
        }

        if (s.whatsapp_phone) {
          setWhatsappPhone(s.whatsapp_phone);
        }
        if (s.whatsapp_enabled) {
          setWhatsappStatus('connected');
        }
        if (s.whatsapp_templates) {
          setWhatsappTemplates(s.whatsapp_templates);
        }
        if (s.whatsapp_toggles) {
          setWhatsappToggles(s.whatsapp_toggles);
        }
      }
    } catch (e) {
      console.warn('Could not load remote notification settings, using local defaults:', e);
    }
  };

  const loadTelegramStats = async () => {
    try {
      const res = await apiClient.get<any>('/api/integrations/telegram/stats');
      if (res.data) {
        setTelegramStats({
          linkedCount: res.data.linkedCount || 0,
          sentToday: res.data.sentToday || 0,
          failedToday: res.data.failedToday || 0,
          skippedCount: res.data.skippedCount || 0,
          totalDispatched: res.data.totalDispatched || 0
        });
        if (res.data.botStatus === 'connected' && !telegramBotStatus) {
          setTelegramBotStatus('connected');
        }
      }
    } catch (e) {
      console.warn('Could not load Telegram stats:', e);
    }
  };

  const loadDeliveryLogs = async () => {
    setIsLoadingLogs(true);
    try {
      const res = await apiClient.get<any>('/api/notifications/delivery-logs');
      if (res.data && Array.isArray(res.data.items)) {
        setDeliveryLogs(res.data.items);
      }
    } catch (e) {
      console.warn('Could not load delivery logs:', e);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (isPushSupported()) {
      const OneSignal = (window as any).OneSignal;
      if (OneSignal) {
        const subId = OneSignal.User?.pushSubscription?.id;
        const optedIn = OneSignal.User?.pushSubscription?.optedIn;
        if (subId && optedIn) {
          setDesktopNotifications(true);
          localStorage.setItem('notification_desktop_enabled', 'true');
          return;
        }
      }

      navigator.serviceWorker?.ready?.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          if (sub) {
            setDesktopNotifications(true);
            localStorage.setItem('notification_desktop_enabled', 'true');
          }
        }).catch(() => {});
      });
    }
  }, []);

  useEffect(() => {
    let intervalId: any;
    if (whatsappStatus === 'connecting' && whatsappSessionId) {
      intervalId = setInterval(async () => {
        try {
          const response = await apiClient.get<{ status: string; qr?: string; phone?: string }>(
            `/whatsapp/pairing-status/${whatsappSessionId}`
          );
          if (response.data) {
            if (response.data.qr) setWhatsappQrCode(response.data.qr);
            if (response.data.status === 'connected') {
              clearInterval(intervalId);
              const pairedPhone = response.data.phone || whatsappPhone;
              setWhatsappStatus('connected');
              localStorage.setItem('whatsapp_status', 'connected');
              localStorage.setItem('whatsapp_phone', pairedPhone);
              const newLog = `${new Date().toLocaleTimeString('ar-IQ')} - تم ربط حساب الواتساب للرقم ${pairedPhone}`;
              setWhatsappLogs((prev) => [newLog, ...prev].slice(0, 10));
            }
          }
        } catch (err) {
          console.error('Error polling WhatsApp pairing:', err);
        }
      }, 2000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [whatsappStatus, whatsappSessionId, whatsappPhone]);

  const handleSelectChannel = (channel: 'WHATSAPP' | 'TELEGRAM' | 'DISABLED') => {
    if (channel === activeChannel) return;
    setPendingChannelSwitch(channel);
    setShowChannelModal(true);
  };

  const confirmChannelSwitch = async () => {
    if (!canManageSettings) return;
    if (!pendingChannelSwitch) return;
    const nextChannel = pendingChannelSwitch;
    setActiveChannel(nextChannel);
    setShowChannelModal(false);
    setPendingChannelSwitch(null);

    try {
      await apiClient.post('/api/notifications/settings', {
        notification_channel: nextChannel,
        whatsapp_enabled: nextChannel === 'WHATSAPP',
        telegram_enabled: nextChannel === 'TELEGRAM',
        telegram_bot_token: telegramToken,
        telegram_bot_username: telegramBotUsername,
        telegram_templates: telegramTemplates,
        telegram_toggles: telegramToggles,
        whatsapp_templates: whatsappTemplates,
        whatsapp_toggles: whatsappToggles,
        whatsapp_phone: whatsappPhone,
        updated_at: Date.now()
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: any) {
      alert('حدث خطأ أثناء تبديل قناة التنبيهات: ' + (e.message || e));
    }
  };

  const handleVerifyTelegramBot = async () => {
    if (!telegramToken.trim()) {
      alert('يرجى إدخال رمز Bot Token أولاً.');
      return;
    }
    setIsVerifyingToken(true);
    setTelegramBotStatus('checking');
    setTelegramStatusMessage('جاري الاتصال بخوادم Telegram API للتحقق من الرمز...');

    try {
      const res = await apiClient.post<any>('/api/integrations/telegram/test-connection', {
        botToken: telegramToken.trim()
      });

      if (res.data && res.data.success) {
        setTelegramBotStatus('connected');
        setTelegramBotUsername(res.data.botUsername || '');
        setTelegramBotName(res.data.botName || '');
        setTelegramStatusMessage(`تم التحقق بنجاح! متصل بالبوت: @${res.data.botUsername}`);

        await apiClient.post('/api/notifications/settings', {
          notification_channel: activeChannel,
          telegram_enabled: activeChannel === 'TELEGRAM',
          telegram_bot_token: telegramToken.trim(),
          telegram_bot_username: res.data.botUsername,
          telegram_bot_name: res.data.botName,
          telegram_templates: telegramTemplates,
          telegram_toggles: telegramToggles
        });
        loadTelegramStats();
      } else {
        setTelegramBotStatus('disconnected');
        setTelegramStatusMessage(res.data?.message || 'فشل التحقق من الـ Token');
      }
    } catch (err: any) {
      setTelegramBotStatus('disconnected');
      setTelegramStatusMessage(err.response?.data?.message || err.message || 'فشل الاتصال بخوادم Telegram');
    } finally {
      setIsVerifyingToken(false);
    }
  };

  const handleSaveTelegramTemplates = async () => {
    if (!canManageSettings) return;
    localStorage.setItem('tg_tpl_days3', telegramTemplates.days3);
    localStorage.setItem('tg_tpl_looming', telegramTemplates.looming);
    localStorage.setItem('tg_tpl_expired', telegramTemplates.expired);
    localStorage.setItem('tg_tpl_renewal', telegramTemplates.renewal);
    localStorage.setItem('tg_toggle_start', String(telegramToggles.start));
    localStorage.setItem('tg_toggle_days3', String(telegramToggles.days3));
    localStorage.setItem('tg_toggle_hours24', String(telegramToggles.hours24));
    localStorage.setItem('tg_toggle_expiredAuto', String(telegramToggles.expiredAuto));

    try {
      await apiClient.post('/api/notifications/settings', {
        notification_channel: activeChannel,
        telegram_enabled: activeChannel === 'TELEGRAM',
        telegram_bot_token: telegramToken,
        telegram_bot_username: telegramBotUsername,
        telegram_bot_name: telegramBotName,
        telegram_templates: telegramTemplates,
        telegram_toggles: telegramToggles,
        updated_at: Date.now()
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save Telegram templates:', err);
      alert('فشل حفظ قوالب تليكرام.');
    }
  };

  const handleSendTelegramTest = async () => {
    if (!testChatId.trim()) {
      alert('يرجى إدخال معرف المحادثة (Chat ID) أولاً.');
      return;
    }
    setIsSendingTgTest(true);
    setTgTestResult(null);

    try {
      const res = await apiClient.post<any>('/api/integrations/telegram/test-message', {
        botToken: telegramToken,
        chatId: testChatId.trim(),
        message: testMessageText
      });

      if (res.data?.success) {
        setTgTestResult({ success: true, message: 'تم إرسال الرسالة الاختبارية عبر Telegram بنجاح! 🚀' });
        loadDeliveryLogs();
        loadTelegramStats();
      } else {
        setTgTestResult({ success: false, message: res.data?.message || 'فشل إرسال الرسالة الاختبارية.' });
      }
    } catch (err: any) {
      setTgTestResult({
        success: false,
        message: err.response?.data?.message || err.message || 'فشل الاتصال بخادم التليكرام لإرسال الرسالة.'
      });
    } finally {
      setIsSendingTgTest(false);
    }
  };

  const insertVariable = (variableStr: string) => {
    setTelegramTemplates(prev => ({
      ...prev,
      [activeTgTemplateKey]: (prev[activeTgTemplateKey] || '') + ' ' + variableStr
    }));
  };

  const getPreviewText = () => {
    const rawTemplate = telegramTemplates[activeTgTemplateKey] || '';
    const sampleCustomer = 'علي حسن كاظم';
    const samplePlan = 'باقة FTTH بريميوم 100 ميغا';
    const sampleExpiry = new Date(Date.now() + 3 * 86400000).toLocaleDateString('ar-IQ');
    const sampleDays = activeTgTemplateKey === 'days3' ? '3' : (activeTgTemplateKey === 'looming' ? '1' : '0');

    return rawTemplate
      .replace(/{{\s*subscriber_name\s*}}/g, sampleCustomer)
      .replace(/{{\s*plan_name\s*}}/g, samplePlan)
      .replace(/{{\s*expiry_date\s*}}/g, sampleExpiry)
      .replace(/{{\s*days_remaining\s*}}/g, sampleDays)
      .replace(/{{\s*username\s*}}/g, 'ali_ftth_99')
      .replace(/{{\s*subscriber_id\s*}}/g, 'SUB-10821')
      .replace(/{اسم_المشترك}/g, sampleCustomer)
      .replace(/{اسم_الباقة}/g, samplePlan)
      .replace(/{تاريخ_الانتهاء}/g, sampleExpiry)
      .replace(/{الايام_المتبقية}/g, sampleDays);
  };

  const handleSaveWhatsappTemplates = async () => {
    if (!canManageSettings) return;
    try {
      await apiClient.post('/api/notifications/settings', {
        notification_channel: activeChannel,
        whatsapp_enabled: activeChannel === 'WHATSAPP',
        whatsapp_phone: whatsappPhone,
        whatsapp_templates: whatsappTemplates,
        whatsapp_toggles: whatsappToggles,
        updated_at: Date.now()
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to sync WhatsApp templates:', err);
    }
  };

  const handleStartConnectWhatsapp = async () => {
    if (!whatsappPhone.trim()) {
      alert('يرجى إدخال رقم الهاتف أولاً.');
      return;
    }
    const newSessionId = 'session_' + Math.random().toString(36).substring(2, 11);
    setWhatsappSessionId(newSessionId);
    setWhatsappStatus('connecting');
    setIsGeneratingWhatsapp(true);
    setWhatsappLoading(true);
    setWhatsappQrCode('');

    try {
      await apiClient.post(`/whatsapp/pair/${newSessionId}`, { phone: whatsappPhone });
      setIsGeneratingWhatsapp(false);
      setWhatsappLoading(false);
    } catch (e: any) {
      alert('فشل بدء عملية الاقتران: ' + (e.message || e));
      setWhatsappStatus('disconnected');
      setWhatsappLoading(false);
    }
  };

  const handleDisconnectWhatsapp = async () => {
    try {
      if (whatsappSessionId) {
        await apiClient.post(`/whatsapp/unpair/${whatsappSessionId}`);
      }
    } catch (e) {}
    setWhatsappStatus('disconnected');
    localStorage.setItem('whatsapp_status', 'disconnected');
    setWhatsappLogs(prev => [`${new Date().toLocaleTimeString('ar-IQ')} - تم قطع الاتصال بحساب الواتساب`, ...prev].slice(0, 10));
  };

  const handleToggleDesktopNotifications = async (val: boolean) => {
    if (val) {
      const subscribe = async () => {
        setDesktopNotifications(true);
        localStorage.setItem('notification_desktop_enabled', 'true');
        try {
          if (isPushSupported()) {
            await subscribeToPushNotifications();
          }
        } catch (err) {
          console.error('Failed to subscribe:', err);
        }
      };

      if (Notification.permission === 'granted') {
        await subscribe();
        return;
      }
      if (Notification.permission === 'denied') {
        alert('لقد قمت بحظر صلاحية الإشعارات مسبقاً. يرجى تفعيلها يدوياً من إعدادات المتصفح.');
        return;
      }
      try {
        const permission = await requestNotificationPermission();
        if (permission === 'granted') {
          await subscribe();
        } else {
          setDesktopNotifications(false);
          localStorage.setItem('notification_desktop_enabled', 'false');
        }
      } catch (err) {
        setDesktopNotifications(false);
      }
    } else {
      setDesktopNotifications(false);
      localStorage.setItem('notification_desktop_enabled', 'false');
      try {
        if (isPushSupported()) await unsubscribeFromPushNotifications();
      } catch (e) {}
    }
  };

  const handleSendTestEmail = async () => {
    if (!email) return;
    setIsSendingEmail(true);
    try {
      await fetch(`https://formsubmit.co/ajax/${email}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          _subject: 'تنبيه تجريبي من بوابة مقاولي FTTH Nexus',
          message: 'هذا البريد للتأكد من عمل نظام التنبيهات التلقائي في التطبيق.',
        }),
      });
      const newLog = `${new Date().toLocaleTimeString('ar-IQ')} - تم إرسال بريد إلكتروني تجريبي إلى ${email}`;
      setSendLogs(prev => [newLog, ...prev].slice(0, 10));
      alert('تم إرسال البريد الإلكتروني التجريبي بنجاح.');
    } catch (err) {
      alert('فشل في إرسال البريد الإلكتروني التجريبي.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!canManageSettings) return;
    localStorage.setItem('notification_email', email);
    localStorage.setItem('notification_email_enabled', String(enabled));
    localStorage.setItem('notification_desktop_enabled', String(desktopNotifications));
    localStorage.setItem('notification_sound_enabled', String(soundAlerts));

    try {
      await apiClient.post('/api/notifications/settings', {
        email,
        email_enabled: enabled,
        desktop_enabled: desktopNotifications,
        sound_enabled: soundAlerts,
        notification_channel: activeChannel,
        updated_at: Date.now()
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to sync notification settings:', err);
    }
  };

  const filteredLogs = deliveryLogs.filter(log => {
    if (logsFilterChannel !== 'ALL' && log.channel !== logsFilterChannel) return false;
    if (logsFilterStatus !== 'ALL' && log.status !== logsFilterStatus) return false;
    return true;
  });

  return (
    <div style={{ padding: '24px 20px', direction: 'rtl', minHeight: '100%', maxWidth: '850px', margin: '0 auto', boxSizing: 'border-box' }}>
      
      <div style={{ marginBottom: '24px', textAlign: 'right' }}>
        <h1 style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '24px', fontWeight: 800, margin: '0 0 6px 0' }}>
          إدارة التنبيهات والقنوات التلقائية (Alerts & Notifications)
        </h1>
        <p style={{ color: '#64748b', fontSize: '13.5px', margin: 0, lineHeight: '1.6' }}>
          تخصيص قنوات إرسال تنبيهات انتهاء الاشتراكات للمشتركين عبر <strong>Telegram</strong> أو <strong>WhatsApp</strong> مع ضمان عدم التكرار وعزل البيانات.
        </p>
      </div>

      <div style={{
        background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.8)',
        border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'}`,
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px -5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowRightLeft size={18} color="#3b82f6" />
            <h3 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '16px', fontWeight: 700, margin: 0 }}>
              قناة التنبيهات الفعالة للمشتركين (Active Notification Channel)
            </h3>
          </div>
          <span style={{
            background: activeChannel === 'TELEGRAM' ? 'rgba(59,130,246,0.15)' : (activeChannel === 'WHATSAPP' ? 'rgba(37,211,102,0.15)' : 'rgba(148,163,184,0.15)'),
            color: activeChannel === 'TELEGRAM' ? '#3b82f6' : (activeChannel === 'WHATSAPP' ? '#25d366' : '#94a3b8'),
            border: `1px solid ${activeChannel === 'TELEGRAM' ? 'rgba(59,130,246,0.3)' : (activeChannel === 'WHATSAPP' ? 'rgba(37,211,102,0.3)' : 'rgba(148,163,184,0.3)')}`,
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 700
          }}>
            {activeChannel === 'TELEGRAM' ? '🚀 تليكرام (Telegram) نشط' : (activeChannel === 'WHATSAPP' ? '💬 واتساب (WhatsApp) نشط' : '⚪ التنبيهات معطلة')}
          </span>
        </div>

        <p style={{ color: '#64748b', fontSize: '12.5px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
          قاعدة النظام: يمكن تفعيل <strong>قناة واحدة فقط</strong> في نفس الوقت لتفادي إرسال إشعارات مكررة للمشترك.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          
          <div
            onClick={() => handleSelectChannel('TELEGRAM')}
            style={{
              cursor: 'pointer',
              borderRadius: '12px',
              padding: '14px',
              border: `2px solid ${activeChannel === 'TELEGRAM' ? '#3b82f6' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
              background: activeChannel === 'TELEGRAM' ? (isLight ? '#eff6ff' : 'rgba(59,130,246,0.1)') : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: '#0088cc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}>
                <Bot size={20} />
              </div>
              <div>
                <div style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '14px', fontWeight: 700 }}>تليكرام (Telegram)</div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>إشعارات رسمية وسريعة عبر البوت</div>
              </div>
            </div>
            {activeChannel === 'TELEGRAM' && <CheckCircle2 size={18} color="#3b82f6" />}
          </div>

          <div
            onClick={() => handleSelectChannel('WHATSAPP')}
            style={{
              cursor: 'pointer',
              borderRadius: '12px',
              padding: '14px',
              border: `2px solid ${activeChannel === 'WHATSAPP' ? '#25d366' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
              background: activeChannel === 'WHATSAPP' ? (isLight ? '#f0fdf4' : 'rgba(37,211,102,0.1)') : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: '#25d366',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}>
                <Send size={18} />
              </div>
              <div>
                <div style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '14px', fontWeight: 700 }}>واتساب (WhatsApp)</div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>إرسال مباشر إلى هاتف المشترك</div>
              </div>
            </div>
            {activeChannel === 'WHATSAPP' && <CheckCircle2 size={18} color="#25d366" />}
          </div>

          <div
            onClick={() => handleSelectChannel('DISABLED')}
            style={{
              cursor: 'pointer',
              borderRadius: '12px',
              padding: '14px',
              border: `2px solid ${activeChannel === 'DISABLED' ? '#94a3b8' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)')}`,
              background: activeChannel === 'DISABLED' ? (isLight ? '#f8fafc' : 'rgba(148,163,184,0.1)') : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}>
                <WifiOff size={18} />
              </div>
              <div>
                <div style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '14px', fontWeight: 700 }}>تعطيل التنبيهات</div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>إيقاف إرسال رسائل التذكير التلقائية</div>
              </div>
            </div>
            {activeChannel === 'DISABLED' && <CheckCircle2 size={18} color="#94a3b8" />}
          </div>

        </div>
      </div>

      {showChannelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: isLight ? '#ffffff' : '#0f172a',
            border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'}`,
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '480px',
            width: '100%',
            direction: 'rtl',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <AlertTriangle size={24} color="#f59e0b" />
              <h3 style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                تأكيد تغيير قناة التنبيهات الفعالة
              </h3>
            </div>
            
            <p style={{ color: isLight ? '#475569' : '#cbd5e1', fontSize: '13.5px', lineHeight: '1.6', marginBottom: '20px' }}>
              أنت على وشك التبديل إلى قناة <strong>{pendingChannelSwitch === 'TELEGRAM' ? 'تليكرام (Telegram)' : (pendingChannelSwitch === 'WHATSAPP' ? 'واتساب (WhatsApp)' : 'التعطيل')}</strong>.
              <br /><br />
              سيؤدي هذا إلى إيقاف إرسال الرسائل التلقائية عبر القناة السابقة تلقائياً في الخلفية.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => { setShowChannelModal(false); setPendingChannelSwitch(null); }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.2)'}`,
                  color: isLight ? '#64748b' : '#94a3b8',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
              <button
                onClick={confirmChannelSwitch}
                style={{
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                تأكيد وتفعيل القناة
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)'}`,
        paddingBottom: '8px',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setActiveTab('telegram')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '10px',
            border: 'none',
            background: activeTab === 'telegram' ? '#0088cc' : 'transparent',
            color: activeTab === 'telegram' ? '#ffffff' : (isLight ? '#475569' : '#94a3b8'),
            fontWeight: activeTab === 'telegram' ? 700 : 500,
            fontSize: '13.5px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap'
          }}
        >
          <Bot size={18} />
          إعدادات تليكرام (Telegram)
          {activeChannel === 'TELEGRAM' && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />}
        </button>

        <button
          onClick={() => setActiveTab('whatsapp')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '10px',
            border: 'none',
            background: activeTab === 'whatsapp' ? '#25d366' : 'transparent',
            color: activeTab === 'whatsapp' ? '#ffffff' : (isLight ? '#475569' : '#94a3b8'),
            fontWeight: activeTab === 'whatsapp' ? 700 : 500,
            fontSize: '13.5px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap'
          }}
        >
          <Send size={16} />
          إعدادات الواتساب (WhatsApp)
          {activeChannel === 'WHATSAPP' && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />}
        </button>

        <button
          onClick={() => setActiveTab('system')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '10px',
            border: 'none',
            background: activeTab === 'system' ? '#6366f1' : 'transparent',
            color: activeTab === 'system' ? '#ffffff' : (isLight ? '#475569' : '#94a3b8'),
            fontWeight: activeTab === 'system' ? 700 : 500,
            fontSize: '13.5px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap'
          }}
        >
          <Bell size={16} />
          إشعارات النظام والبريد
        </button>

        <button
          onClick={() => { setActiveTab('logs'); loadDeliveryLogs(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '10px',
            border: 'none',
            background: activeTab === 'logs' ? '#0ea5e9' : 'transparent',
            color: activeTab === 'logs' ? '#ffffff' : (isLight ? '#475569' : '#94a3b8'),
            fontWeight: activeTab === 'logs' ? 700 : 500,
            fontSize: '13.5px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap'
          }}
        >
          <Clock size={16} />
          سجل التسليم (Delivery Logs)
        </button>
      </div>

      {activeTab === 'telegram' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '14px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Bot size={22} color="#0088cc" />
                <div>
                  <h2 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                    ربط بوت التليكرام الرسمي (Telegram Bot API)
                  </h2>
                  <p style={{ color: '#64748b', fontSize: '12px', margin: '2px 0 0 0' }}>
                    يتم الإرسال عبر خوادم Telegram Bot API الرسمية دون الحاجة لجلسات هاتف مفتوحة.
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: telegramBotStatus === 'connected' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${telegramBotStatus === 'connected' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                padding: '6px 14px',
                borderRadius: '20px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: telegramBotStatus === 'connected' ? '#10b981' : '#ef4444',
                  boxShadow: `0 0 8px ${telegramBotStatus === 'connected' ? '#10b981' : '#ef4444'}`
                }} />
                <span style={{
                  color: telegramBotStatus === 'connected' ? '#10b981' : '#ef4444',
                  fontSize: '12px',
                  fontWeight: 700
                }}>
                  {telegramBotStatus === 'connected' ? (telegramBotUsername ? `@${telegramBotUsername}` : 'البوت متصل بنجاح') : 'غير متصل'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', color: isLight ? '#475569' : '#94a3b8', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                رمز Bot Token (من @BotFather)
              </label>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type={showToken ? 'text' : 'password'}
                    placeholder="مثال: 1234567890:ABCDefGhIjKlMnOpQrStUvWxYz..."
                    value={telegramToken}
                    onChange={(e) => setTelegramToken(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '11px 40px 11px 14px',
                      borderRadius: '8px',
                      background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                      border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                      color: isLight ? '#0f172a' : '#e2e8f0',
                      fontSize: '13.5px',
                      outline: 'none',
                      direction: 'ltr',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer'
                    }}
                  >
                    {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <button
                  onClick={handleVerifyTelegramBot}
                  disabled={isVerifyingToken || !telegramToken}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(90deg, #0088cc, #0284c7)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0 20px',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: isVerifyingToken || !telegramToken ? 'not-allowed' : 'pointer',
                    opacity: isVerifyingToken || !telegramToken ? 0.6 : 1
                  }}
                >
                  <RefreshCw size={14} className={isVerifyingToken ? 'animate-spin' : ''} />
                  {isVerifyingToken ? 'جاري التحقق...' : 'تحقق من الاتصال'}
                </button>
              </div>

              {telegramStatusMessage && (
                <div style={{
                  marginTop: '10px',
                  color: telegramBotStatus === 'connected' ? '#10b981' : '#f87171',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {telegramStatusMessage}
                </div>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '10px',
              paddingTop: '16px',
              borderTop: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)'}`
            }}>
              <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>المشتركين المرتبطين</div>
                <div style={{ color: '#3b82f6', fontSize: '18px', fontWeight: 800 }}>{telegramStats.linkedCount}</div>
              </div>
              <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>مرسلة اليوم</div>
                <div style={{ color: '#10b981', fontSize: '18px', fontWeight: 800 }}>{telegramStats.sentToday}</div>
              </div>
              <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>فاشلة اليوم</div>
                <div style={{ color: '#ef4444', fontSize: '18px', fontWeight: 800 }}>{telegramStats.failedToday}</div>
              </div>
              <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '4px' }}>لم يتم ربطهم (تخطي)</div>
                <div style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 800 }}>{telegramStats.skippedCount}</div>
              </div>
            </div>
          </div>

          <div style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '14px',
            padding: '24px'
          }}>
            <h3 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0' }}>
              قوالب رسائل تليكرام والمعاينة المباشرة (Templates & Live Preview)
            </h3>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { key: 'days3', label: '⏳ تنبيه قبل 3 أيام' },
                { key: 'looming', label: '⚠️ تنبيه قبل 24 ساعة' },
                { key: 'expired', label: '🚫 انتهاء الاشتراك' },
                { key: 'renewal', label: '✅ تأكيد التجديد' },
              ].map((tItem) => (
                <button
                  key={tItem.key}
                  type="button"
                  onClick={() => setActiveTgTemplateKey(tItem.key as any)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${activeTgTemplateKey === tItem.key ? '#0088cc' : (isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)')}`,
                    background: activeTgTemplateKey === tItem.key ? 'rgba(0,136,204,0.15)' : 'transparent',
                    color: activeTgTemplateKey === tItem.key ? '#0088cc' : (isLight ? '#475569' : '#94a3b8'),
                    fontSize: '12.5px',
                    fontWeight: activeTgTemplateKey === tItem.key ? 700 : 500,
                    cursor: 'pointer'
                  }}
                >
                  {tItem.label}
                </button>
              ))}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ color: '#64748b', fontSize: '11.5px', marginBottom: '6px' }}>
                اضغط لإدراج المتغيرات الديناميكية في القالب:
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { tag: '{{subscriber_name}}', label: 'اسم المشترك' },
                  { tag: '{{expiry_date}}', label: 'تاريخ الانتهاء' },
                  { tag: '{{days_remaining}}', label: 'الأيام المتبقية' },
                  { tag: '{{plan_name}}', label: 'اسم الباقة' },
                  { tag: '{{username}}', label: 'اسم المستخدم' },
                  { tag: '{{subscriber_id}}', label: 'رقم المشترك' },
                ].map((v) => (
                  <button
                    key={v.tag}
                    type="button"
                    onClick={() => insertVariable(v.tag)}
                    style={{
                      background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '6px',
                      padding: '3px 8px',
                      color: '#0284c7',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    + {v.label} ({v.tag})
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12.5px', marginBottom: '6px' }}>
                  محرر نص الرسالة:
                </label>
                <textarea
                  value={telegramTemplates[activeTgTemplateKey]}
                  onChange={(e) => setTelegramTemplates(prev => ({ ...prev, [activeTgTemplateKey]: e.target.value }))}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12.5px', marginBottom: '6px' }}>
                  معاينة مظهر الرسالة في Telegram:
                </label>
                <div style={{
                  background: isLight ? '#8db2cb' : '#17212b',
                  borderRadius: '10px',
                  padding: '16px',
                  minHeight: '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`
                }}>
                  <div style={{
                    background: isLight ? '#ffffff' : '#242f3d',
                    borderRadius: '12px 12px 2px 12px',
                    padding: '12px 14px',
                    maxWidth: '90%',
                    alignSelf: 'flex-end',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{
                      color: isLight ? '#0f172a' : '#f5f5f5',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap',
                      textAlign: 'right'
                    }}>
                      {getPreviewText()}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '4px',
                      color: '#64748b',
                      fontSize: '10px',
                      marginTop: '6px'
                    }}>
                      <span>{new Date().toLocaleTimeString('ar-IQ', { hour: '2-digit', minute: '2-digit' })}</span>
                      <span>✓✓</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '14px',
              padding: '16px 0',
              borderTop: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)'}`,
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isLight ? '#334155' : '#cbd5e1', fontSize: '13px' }}>إرسال تنبيه قبل 3 أيام</span>
                <ToggleSwitch checked={telegramToggles.days3} onChange={(c) => setTelegramToggles(p => ({ ...p, days3: c }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isLight ? '#334155' : '#cbd5e1', fontSize: '13px' }}>إرسال تنبيه قبل 24 ساعة</span>
                <ToggleSwitch checked={telegramToggles.hours24} onChange={(c) => setTelegramToggles(p => ({ ...p, hours24: c }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isLight ? '#334155' : '#cbd5e1', fontSize: '13px' }}>إرسال تنبيه عند التجديد</span>
                <ToggleSwitch checked={telegramToggles.start} onChange={(c) => setTelegramToggles(p => ({ ...p, start: c }))} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: isLight ? '#334155' : '#cbd5e1', fontSize: '13px' }}>إرسال تنبيه عند الانتهاء</span>
                <ToggleSwitch checked={telegramToggles.expiredAuto} onChange={(c) => setTelegramToggles(p => ({ ...p, expiredAuto: c }))} />
              </div>
            </div>

            <button
              onClick={handleSaveTelegramTemplates}
              disabled={!canManageSettings}
              title={!canManageSettings ? 'غير مصرح بتعديل الإعدادات (settings.manage)' : 'حفظ قوالب وإعدادات تليكرام'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(90deg, #0088cc, #0284c7)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                color: '#fff',
                fontSize: '13.5px',
                fontWeight: 700,
                cursor: canManageSettings ? 'pointer' : 'not-allowed',
                opacity: canManageSettings ? 1 : 0.5,
              }}
            >
              <Save size={15} />
              حفظ قوالب وإعدادات تليكرام
            </button>
          </div>

          <div style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '14px',
            padding: '24px'
          }}>
            <h3 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '16px', fontWeight: 700, margin: '0 0 14px 0' }}>
              إرسال رسالة اختبارية (Test Message Sender)
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12.5px', marginBottom: '6px' }}>
                  معرف المحادثة (Telegram Chat ID)
                </label>
                <input
                  type="text"
                  placeholder="مثال: 987654321"
                  value={testChatId}
                  onChange={(e) => setTestChatId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    outline: 'none',
                    direction: 'ltr',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12.5px', marginBottom: '6px' }}>
                  نص الرسالة الاختبارية
                </label>
                <input
                  type="text"
                  value={testMessageText}
                  onChange={(e) => setTestMessageText(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button
                onClick={handleSendTelegramTest}
                disabled={isSendingTgTest || !testChatId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isSendingTgTest || !testChatId ? 'not-allowed' : 'pointer',
                  opacity: isSendingTgTest || !testChatId ? 0.5 : 1
                }}
              >
                <Send size={14} className={isSendingTgTest ? 'animate-spin' : ''} />
                {isSendingTgTest ? 'جاري الإرسال...' : 'إرسال رسالة اختبارية'}
              </button>

              {tgTestResult && (
                <span style={{
                  color: tgTestResult.success ? '#10b981' : '#f87171',
                  fontSize: '13px',
                  fontWeight: 600
                }}>
                  {tgTestResult.message}
                </span>
              )}
            </div>
          </div>

        </div>
      )}

      {activeTab === 'whatsapp' && (
        <div
          style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '14px',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', justifyContent: 'flex-start' }}>
            <Send size={20} color="#25D366" />
            <h2 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '18px', fontWeight: 700, margin: 0 }}>
              تنبيهات الواتساب (WhatsApp Alerts)
            </h2>
          </div>

          <div style={{
            background: whatsappStatus === 'connected' ? 'rgba(16,185,129,0.06)' : (whatsappStatus === 'connecting' ? 'rgba(217,119,6,0.06)' : 'rgba(239,68,68,0.04)'),
            border: `1px solid ${whatsappStatus === 'connected' ? 'rgba(16,185,129,0.2)' : (whatsappStatus === 'connecting' ? 'rgba(217,119,6,0.2)' : 'rgba(239,68,68,0.15)')}`,
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            direction: 'rtl'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: whatsappStatus === 'connected' ? '#10b981' : (whatsappStatus === 'connecting' ? '#f59e0b' : '#ef4444'),
                boxShadow: `0 0 10px ${whatsappStatus === 'connected' ? '#10b981' : (whatsappStatus === 'connecting' ? '#f59e0b' : '#ef4444')}`
              }} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{
                  color: whatsappStatus === 'connected' ? '#10b981' : (whatsappStatus === 'connecting' ? '#f59e0b' : '#ef4444'),
                  fontWeight: 700,
                  fontSize: '14px'
                }}>
                  {whatsappStatus === 'connected' ? 'حالة الربط: متصل بنجاح' : (whatsappStatus === 'connecting' ? 'بانتظار مسح الرمز...' : 'حالة الربط: غير متصل')}
                </span>
                <span style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                  {whatsappStatus === 'connected' ? `الرقم المرتبط: ${whatsappPhone}` : 'قم بربط حسابك لتفعيل رسائل التذكير التلقائية عبر الواتساب.'}
                </span>
              </div>
            </div>

            <div>
              {whatsappStatus === 'connected' ? (
                <button
                  onClick={handleDisconnectWhatsapp}
                  style={{
                    background: 'rgba(239,68,68,0.15)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: '#f87171',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  قطع الاتصال (Disconnect)
                </button>
              ) : (
                whatsappStatus === 'disconnected' && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="رقم الهاتف (07XXXXXXXX)"
                      value={whatsappPhone}
                      onChange={(e) => setWhatsappPhone(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                        color: isLight ? '#0f172a' : '#e2e8f0',
                        fontSize: '13px',
                        direction: 'ltr',
                        outline: 'none',
                      }}
                    />
                    <button
                      onClick={handleStartConnectWhatsapp}
                      disabled={whatsappLoading}
                      style={{
                        background: 'linear-gradient(90deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        color: '#fff',
                        fontSize: '13px',
                        cursor: whatsappLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {whatsappLoading ? 'جاري التوليد...' : 'بدء الربط'}
                    </button>
                  </div>
                )
              )}
            </div>
          </div>

          {whatsappStatus === 'connecting' && whatsappQrCode && (
            <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(5,15,30,0.4)', borderRadius: '12px', marginBottom: '20px' }}>
              <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>
                افتح تطبيق WhatsApp على هاتفك واذهب إلى الأجهزة المرتبطة ثم امسح الرمز:
              </p>
              <img src={whatsappQrCode} alt="WhatsApp QR Code" style={{ width: '220px', height: '220px', borderRadius: '8px' }} />
            </div>
          )}

          <div style={{ borderTop: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`, paddingTop: '20px' }}>
            <h3 style={{ color: isLight ? '#0f172a' : '#cbd5e1', fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>
              قوالب رسائل الواتساب
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12.5px', marginBottom: '6px' }}>قالب التذكير قبل 3 أيام</label>
                <textarea
                  value={whatsappTemplates.days3}
                  onChange={e => setWhatsappTemplates(p => ({ ...p, days3: e.target.value }))}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12.5px', marginBottom: '6px' }}>قالب التذكير قبل 24 ساعة</label>
                <textarea
                  value={whatsappTemplates.looming}
                  onChange={e => setWhatsappTemplates(p => ({ ...p, looming: e.target.value }))}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <button
              onClick={handleSaveWhatsappTemplates}
              disabled={!canManageSettings}
              title={!canManageSettings ? 'غير مصرح بتعديل الإعدادات (settings.manage)' : 'حفظ قوالب الواتساب'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(90deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: canManageSettings ? 'pointer' : 'not-allowed',
                opacity: canManageSettings ? 1 : 0.5,
              }}
            >
              <Save size={14} />
              حفظ قوالب الواتساب
            </button>
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div
          style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: '14px',
            padding: '24px',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
            <Bell size={20} color="#3b82f6" />
            <h2 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '18px', fontWeight: 700, margin: 0 }}>
              التنبيهات البريدية وسطح المكتب (Email & Desktop Alerts)
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: isLight ? '#334155' : '#94a3b8', fontSize: '14px' }}>تفعيل إشعارات سطح المكتب (Desktop Notifications)</span>
              <ToggleSwitch checked={desktopNotifications} onChange={handleToggleDesktopNotifications} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: isLight ? '#334155' : '#94a3b8', fontSize: '14px' }}>تفعيل التنبيهات الصوتية للتذاكر (Sound Alerts)</span>
              <ToggleSwitch checked={soundAlerts} onChange={setSoundAlerts} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: isLight ? '#334155' : '#94a3b8', fontSize: '14px' }}>تنبيهات التذاكر والطلبات عبر البريد (Email Notifications)</span>
              <ToggleSwitch checked={enabled} onChange={setEnabled} />
            </div>
          </div>

          {enabled && (
            <div style={{ borderTop: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`, paddingTop: '20px' }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '13px', marginBottom: '8px' }}>
                البريد الإلكتروني المستهدف لتلقي التنبيهات
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="email"
                  placeholder="example@mail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '14px',
                    outline: 'none',
                    direction: 'ltr',
                  }}
                />
                <button
                  onClick={handleSendTestEmail}
                  disabled={isSendingEmail || !email}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: '8px',
                    padding: '0 16px',
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    cursor: isSendingEmail || !email ? 'not-allowed' : 'pointer',
                    opacity: isSendingEmail || !email ? 0.5 : 1,
                  }}
                >
                  <Send size={14} />
                  إرسال بريد تجريبي
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: '20px' }}>
            <button
              onClick={handleSaveNotifications}
              disabled={!canManageSettings}
              title={!canManageSettings ? 'غير مصرح بتعديل الإعدادات (settings.manage)' : 'حفظ إعدادات النظام'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                color: '#fff',
                fontSize: '13.5px',
                fontWeight: 700,
                cursor: canManageSettings ? 'pointer' : 'not-allowed',
                opacity: canManageSettings ? 1 : 0.5,
              }}
            >
              <Save size={15} />
              حفظ إعدادات النظام
            </button>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: '14px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={20} color="#0ea5e9" />
              <h3 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                سجل إرسال وتوصيل التنبيهات (Notification Delivery Logs)
              </h3>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={logsFilterChannel}
                onChange={(e) => setLogsFilterChannel(e.target.value as any)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: isLight ? '#f1f5f9' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  fontSize: '12px'
                }}
              >
                <option value="ALL">جميع القنوات</option>
                <option value="TELEGRAM">تليكرام (Telegram)</option>
                <option value="WHATSAPP">واتساب (WhatsApp)</option>
              </select>

              <select
                value={logsFilterStatus}
                onChange={(e) => setLogsFilterStatus(e.target.value as any)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: isLight ? '#f1f5f9' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  fontSize: '12px'
                }}
              >
                <option value="ALL">جميع الحالات</option>
                <option value="SENT">ناجح (Sent)</option>
                <option value="FAILED">فاشل (Failed)</option>
                <option value="SKIPPED">تم التخطي (Skipped)</option>
              </select>

              <button
                onClick={loadDeliveryLogs}
                disabled={isLoadingLogs}
                style={{
                  background: 'rgba(59,130,246,0.15)',
                  border: '1px solid rgba(59,130,246,0.3)',
                  color: '#3b82f6',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <RefreshCw size={12} className={isLoadingLogs ? 'animate-spin' : ''} />
                تحديث
              </button>
            </div>
          </div>

          {filteredLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 12px', color: '#64748b', fontSize: '13px' }}>
              لا توجد سجلات توصيل مسجلة حالياً.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '12.5px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)'}`, color: '#64748b' }}>
                    <th style={{ padding: '10px 8px' }}>الوقت / التاريخ</th>
                    <th style={{ padding: '10px 8px' }}>المشترك</th>
                    <th style={{ padding: '10px 8px' }}>القناة</th>
                    <th style={{ padding: '10px 8px' }}>نوع التنبيه</th>
                    <th style={{ padding: '10px 8px' }}>المستلم</th>
                    <th style={{ padding: '10px 8px' }}>الحالة</th>
                    <th style={{ padding: '10px 8px' }}>التفاصيل</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log, idx) => {
                    const timeStr = log.sentAt || log.createdAt
                      ? new Date(log.sentAt || log.createdAt || 0).toLocaleString('ar-IQ')
                      : '—';
                    
                    return (
                      <tr
                        key={log.id || idx}
                        style={{
                          borderBottom: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)'}`,
                          color: isLight ? '#334155' : '#cbd5e1'
                        }}
                      >
                        <td style={{ padding: '10px 8px', whiteSpace: 'nowrap', fontSize: '11.5px' }}>{timeStr}</td>
                        <td style={{ padding: '10px 8px', fontWeight: 600 }}>{log.customerName || log.subscriberId}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{
                            background: log.channel === 'TELEGRAM' ? 'rgba(0,136,204,0.15)' : 'rgba(37,211,102,0.15)',
                            color: log.channel === 'TELEGRAM' ? '#0088cc' : '#25d366',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700
                          }}>
                            {log.channel}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{
                            background: 'rgba(255,255,255,0.06)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px'
                          }}>
                            {log.notificationType === 'EXPIRY_3D' ? 'قبل 3 أيام' : (log.notificationType === 'EXPIRY_1D' ? 'قبل 24 ساعة' : log.notificationType)}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', direction: 'ltr', textAlign: 'left', fontSize: '11.5px', fontFamily: 'monospace' }}>
                          {log.recipient}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{
                            background: log.status === 'SENT' ? 'rgba(16,185,129,0.15)' : (log.status === 'FAILED' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'),
                            color: log.status === 'SENT' ? '#10b981' : (log.status === 'FAILED' ? '#ef4444' : '#f59e0b'),
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700
                          }}>
                            {log.status === 'SENT' ? '✓ ناجح' : (log.status === 'FAILED' ? '✗ فاشل' : '⚪ تخطي')}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '11px', color: '#64748b' }}>
                          {log.errorMessage || (log.externalMessageId ? `Msg ID: ${log.externalMessageId}` : '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Global save feedback toast */}
      {saveSuccess && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '24px',
          background: 'rgba(16,185,129,0.95)',
          color: '#ffffff',
          padding: '12px 20px',
          borderRadius: '10px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13.5px',
          fontWeight: 700,
          zIndex: 9999
        }}>
          <CheckCircle2 size={18} />
          تم حفظ وتطبيق جميع التعديلات بنجاح!
        </div>
      )}

    </div>
  );
};

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ checked, onChange }) => {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: '46px',
        height: '24px',
        borderRadius: '999px',
        background: checked ? '#3b82f6' : 'rgba(255,255,255,0.15)',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: '3px',
          left: checked ? '25px' : '3px',
          transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </div>
  );
};

export default AlertsPage;
